/**
 * Compact language toggle — appears in the public top-bar + the
 * authed-app top-bar. Two visual variants:
 *
 *   - `chip`  (default): bordered dropdown, fits next to other chips
 *   - `inline`: ghost button + flags side by side, for the auth shell
 *
 * Persistence is automatic via i18next's localStorage detector
 * (see `i18n/index.ts`).
 */
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/i18n'

interface LanguageSwitcherProps {
  variant?: 'chip' | 'inline'
  className?: string
}

export function LanguageSwitcher({
  variant = 'chip', className,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage)
                ?? SUPPORTED_LANGUAGES[0]

  // Close on outside-click — small enough not to need a real popover lib
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function pick(code: LanguageCode) {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  if (variant === 'inline') {
    return (
      <div className={cn('inline-flex items-center gap-1', className)}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => pick(lang.code)}
            className={cn(
              'px-2 py-1 text-xs font-mono uppercase tracking-wider',
              'transition-colors rounded',
              lang.code === active.code
                ? 'text-fire'
                : 'text-textFaint hover:text-textDim',
            )}
            aria-label={lang.label}
          >
            {lang.code}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-elevated border border-line hover:border-lineHi text-xs text-textDim rounded transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={12} />
        <span className="font-mono uppercase">{active.code}</span>
        <ChevronDown size={11} className="text-textFaint" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1 z-20 min-w-[140px] bg-surface border border-line rounded shadow-soft py-1"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === active.code}
              onClick={() => pick(lang.code)}
              className={cn(
                'w-full text-left px-3 py-1.5 text-sm flex items-center gap-2',
                'transition-colors',
                lang.code === active.code
                  ? 'bg-fire-faint text-fire'
                  : 'text-text hover:bg-elevated',
              )}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
